<script setup>
import { ref, onBeforeMount } from "vue";
import { useRouter } from "vue-router";
import { format } from "date-fns";

import SubpageHeader from "@/components/organisms/header/SubpageHeader.vue";
import MainSection from "@/components/templates/mainSection.vue";
import MainFooter from "@/components/organisms/footer/MainFooter.vue";
import BodyContain from "@/components/molcules/layout/bodyContain.vue";
import { route } from "@/router/route";
import PretendardText from "@/components/atoms/text/PretendardText.vue";
import { displayHooks } from "@/hooks/useDisplay";
import MenuApi from "@/api/menu";
import SharingApi from "@/api/sharing";
import { getRouteByHomeLink, findMenuSection } from "@/util/menuUtil";
import { useToast } from "vue-toastification";
import EmptyTable from "@/components/organisms/emptyTable.vue";
import BtmPagination from "@/components/molcules/pagination/btmPagination.vue";
import SearchInput from "@/components/molcules/input/searchInput.vue";

const toast = useToast();
const router = useRouter();

let useDisplayRef = ref(displayHooks);

let menuValue = ref("");

let last = ref(false);
let first = ref(false);
let page = ref(0);
let maxPage = ref(0);
let step = ref(0);
let total = ref(0);
let listSize = ref(5);
let paginationSize = ref(5);

let keyword = ref("");
let mainSectionMenu = ref([]);

const goPrevPage = () => {
  if (!first.value) {
    page.value--;
    getApi();
  } else return;

  if (page.value < paginationSize.value * step.value) {
    step.value--;
  }
};

const goNextPage = () => {
  if (!last.value) {
    page.value++;
    getApi();
  } else return;

  if (page.value + 1 > paginationSize.value * (step.value + 1)) {
    step.value++;
  }
};

const goPage = (index) => {
  page.value = index;
  getApi();
};

onBeforeMount(() => {
  getMenu();
  getApi();
});

const getMenu = async () => {
  try {
    const arr = [];

    const data = {
      exposureFlag: true,
    };

    const response = (await MenuApi.get(data)).data.data;

    if (response) {
      const sectionMenu = findMenuSection(response.content, "sharing");
      const subMenu = sectionMenu?.subMenus || [];
      subMenu.map((item, index) => {
        if (item.contentsFlag) {
          arr.push({
            value: item.title,
            label: item.title,
            routeName: route.board + `/${item.boardId}`,
          });
        } else {
          arr.push({
            value: item.title,
            label: item.title,
            routeName: getRouteByHomeLink(item.homeLink) || route[`sharing${index + 1}`],
          });
        }

        if (item.homeLink === "/out-come1") {
          menuValue.value = item.title;
        }
        return;
      });

      mainSectionMenu.value = arr;
    }
  } catch (e) {
    toast("서버에 문제가 있습니다. 잠시후 다시 시도하세요.", {
      toastClassName: "toast-wrapper",
      position: "top-center",
      timeout: 2000,
      hideProgressBar: true,
      closeButton: false,
      icon: false,
    });
  }
};

const list = ref([]);

const getApi = async (initial) => {
  try {
    if (initial) {
      total.value = 0;
      page.value = 0;
      step.value = 0;
      paginationSize.value = 5;
    }
    const response = await SharingApi.get({
      size: listSize.value,
      page: page.value,
      keyword: keyword.value,
      useFlag: true,
    });

    if (response.data.data) {
      last.value = response.data?.data?.last;
      first.value = response.data?.data?.first;
      list.value = response.data?.data?.content;
      total.value = response.data.data.totalElements;

      maxPage.value = Math.ceil(total.value / listSize.value);
      if (maxPage.value <= paginationSize.value) {
        paginationSize.value = maxPage.value;
      }
    }
  } catch (e) {
    toast("서버에 문제가 있습니다. 잠시후 다시 시도하세요.", {
      toastClassName: "toast-wrapper",
      position: "top-center",
      timeout: 2000,
      hideProgressBar: true,
      closeButton: false,
      icon: false,
    });
  }
};

const detailRouter = (id) => {
  router.push({ path: `${route.sharingDetail}/${id}` });
};

const stripHtml = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};
</script>

<template>
  <SubpageHeader />
  <MainSection
    bgUrl="sharing"
    :options="mainSectionMenu"
    :menuValue="menuValue"
    mainHeader="성과공유"
    subHeader01="Home"
    subHeader02="성과공유"
  />

  <BodyContain
    :style="`padding-bottom: ${useDisplayRef.isMobile ? '60px' : '120px'}`"
  >
    <div
      :class="useDisplayRef.isMobile ? 'flex-col' : 'flex-row-between'"
      :style="useDisplayRef.isMobile ? 'margin-bottom: 30px' : 'margin-bottom: 50px'"
    >
      <PretendardText class="intro-title" style="border-bottom: none; margin-bottom: 0">우수성과</PretendardText>
      <SearchInput
        v-model="keyword"
        placeholder="검색어를 입력하세요."
        @click="getApi(true)"
      />
    </div>
    <div class="list-border" />
    <EmptyTable v-if="list.length <= 0" />
    <div v-else class="card-list">
      <div
        v-for="(item, index) in list"
        :key="index"
        class="card"
        @click="detailRouter(item.id)"
      >
        <div class="card-body">
          <PretendardText class="card-title">{{ item.title }}</PretendardText>
          <PretendardText class="card-preview">{{
            stripHtml(item.content).slice(0, 100)
          }}</PretendardText>
        </div>
        <PretendardText class="card-date">{{
          format(new Date(item.createdAt), "yyyy-MM-dd")
        }}</PretendardText>
      </div>
    </div>
    <BtmPagination
      :page="page"
      :maxPage="maxPage"
      :step="step"
      :last="last"
      :first="first"
      :total="total"
      :listSize="listSize"
      :paginationSize="paginationSize"
      @goPrevPage="goPrevPage"
      @goNextPage="goNextPage"
      @goPage="goPage"
    />
    <PretendardText class="intro-title" style="margin-top: 80px"
      >우수 운영 현황</PretendardText
    >
    <div class="status-wrapper">
      <PretendardText class="text-14"
        >해당 대시보드는 올 해 목표값 대비 달성값으로
        표현됩니다.</PretendardText
      >
      <div class="status-box-wrapper">
        <div class="box">
          <PretendardText class="text-16">
            융합인재 양성분야 교육과정 개발(건)
          </PretendardText>
          <div class="status-bar-container">
            <div class="status-bar-wrapper">
              <div
                class="status-bar"
                style="
                  height: 50%;
                  background: linear-gradient(
                    171deg,
                    #04b0e2 1.7%,
                    #00c6d2 100%
                  );
                "
              >
                <PretendardText class="value">00건</PretendardText>
              </div>
            </div>
            <div class="status-bar-wrapper">
              <div
                class="status-bar"
                style="
                  height: 100%;
                  background: linear-gradient(
                    171deg,
                    #04b0e2 1.7%,
                    #00c6d2 100%
                  );
                "
              >
                <PretendardText class="value">00건</PretendardText>
              </div>
            </div>
          </div>
          <div class="bottom-wrapper">
            <PretendardText class="text-14" style="#111"
              >교육과정</PretendardText
            >
            <PretendardText class="text-14" style="#111"
              >정규 교과목</PretendardText
            >
          </div>
        </div>
        <div class="box">
          <PretendardText class="text-16">
            융합인재 양성분야 교육과정 운영(건)
          </PretendardText>
          <div class="status-bar-container">
            <div class="status-bar-wrapper">
              <div
                class="status-bar"
                style="
                  height: 50%;
                  background: linear-gradient(
                    170deg,
                    #8f6cf4 1.28%,
                    #04b0e2 100%
                  );
                "
              >
                <PretendardText class="value">00건</PretendardText>
              </div>
            </div>
            <div class="status-bar-wrapper">
              <div
                class="status-bar"
                style="
                  height: 100%;
                  background: linear-gradient(
                    170deg,
                    #8f6cf4 1.28%,
                    #04b0e2 100%
                  );
                "
              >
                <PretendardText class="value">00건</PretendardText>
              </div>
            </div>
          </div>
          <div class="bottom-wrapper">
            <PretendardText class="text-14" style="#111"
              >교육과정</PretendardText
            >
            <PretendardText class="text-14" style="#111"
              >정규 교과목</PretendardText
            >
          </div>
        </div>
        <div class="box">
          <PretendardText class="text-16">
            융합인재 양성분야 정규 교과목 이수자(명)
          </PretendardText>
          <div class="status-bar-container">
            <div class="status-bar-wrapper">
              <div
                class="status-bar"
                style="
                  height: 80%;
                  background: linear-gradient(
                    171deg,
                    #04b0e2 1.7%,
                    #00c6d2 100%
                  );
                "
              >
                <PretendardText class="value">00건</PretendardText>
              </div>
            </div>
          </div>
          <div
            class="bottom-wrapper"
            style="justify-content: center; padding-left: 0"
          >
            <PretendardText class="text-14" style="#111"
              >정규 교과목</PretendardText
            >
          </div>
        </div>
      </div>
    </div>
  </BodyContain>
  <MainFooter />
</template>

<style lang="scss" scoped>
.intro-title {
  width: 100%;
  color: $black;
  font-size: 32px;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.6px;
  padding-bottom: 20px;
  border-bottom: 1px solid $line;
  margin-bottom: 50px;

  @include mobile {
    font-size: 20px;
    margin-bottom: 30px;
  }
}

.list-border {
  width: 100%;
  border-bottom: 1px solid $line;
  margin-bottom: 50px;

  @include mobile {
    margin-bottom: 30px;
  }
}

.card-list {
  width: 100%;
  border-top: 1px solid $line;
}

.card {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 20px 15px;
  border-bottom: 1px solid $line;
  cursor: pointer;
  box-sizing: border-box;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f9f9f9;
  }

  .card-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow: hidden;
  }

  .card-title {
    color: $black;
    font-size: 16px;
    font-weight: 600;
    line-height: 120%;
    letter-spacing: -0.6px;

    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .card-preview {
    color: $g03;
    font-size: 14px;
    font-weight: 400;
    line-height: 150%;
    letter-spacing: -0.6px;

    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .card-date {
    flex-shrink: 0;
    color: #717377;
    font-size: 14px;
    font-weight: 400;
    line-height: 16.8px;
    letter-spacing: -0.6px;

    @include mobile {
      display: none;
    }
  }
}

.text-16 {
  color: $black;
  font-size: 16px;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.6px;
}

.text-14 {
  color: $g03;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: -0.6px;
}

.status-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 30px;

  .status-box-wrapper {
    width: 100%;
    display: flex;
    gap: 23px;

    @include mobile {
      flex-direction: column;
      gap: 20px;
    }

    .box {
      width: 100%;
      background: $g08;
      padding: 25px;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }

  .status-bar-container {
    width: 146px;
    display: flex;
    justify-content: center;
    gap: 20px;
    border-bottom: 1px solid #d9d9d9;
    padding: 0 3px;
    margin-top: 21px;
  }

  .status-bar-wrapper {
    width: 60px;
    height: 122px;
    border-radius: 10px 10px 0px 0px;
    background: $g06;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;

    .status-bar {
      width: 100%;
      border-radius: 10px 10px 0px 0px;
      display: flex;
      justify-content: center;
      padding-top: 10px;

      .value {
        color: $white;
        text-align: center;
        font-size: 14px;
        font-weight: 700;
        line-height: 22px;
      }
    }
  }
}

.bottom-wrapper {
  width: 146px;
  display: flex;
  gap: 26px;
  padding-left: 9px;
  margin-top: 11px;
}
</style>
